import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PanoramaFishEyeRoundedIcon from '@mui/icons-material/PanoramaFishEyeRounded';
import { abyssColors } from '@/theme/colors';

interface TemporalAnalysisFilterCapsulesProps {
  data: string[];
  setFilteredSelectedDeployments: Dispatch<SetStateAction<string[]>>;
}

export const TemporalAnalysisFilterCapsules = ({
  data,
  setFilteredSelectedDeployments,
}: TemporalAnalysisFilterCapsulesProps) => {
  const [checkedCapsules, setCheckedCapsules] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (data.length > 0) {
      setFilteredSelectedDeployments(data);
      setCheckedCapsules(Object.fromEntries(data.map((name) => [name, true])));
    }
  }, [data, setFilteredSelectedDeployments]);

  const handleCheckChange = (name: string) => {
    setFilteredSelectedDeployments((previous) => {
      // prevents removal if only one item is selected
      if (previous.length === 1 && previous.includes(name)) {
        return previous; // dont remove the last item
      }

      const newSelectedDeployments = previous.includes(name)
        ? previous.filter((item) => item !== name)
        : [...previous, name];
      return newSelectedDeployments;
    });

    setCheckedCapsules((previous) => {
      const updatedCapsules = { ...previous };
      updatedCapsules[name] = !previous[name];

      // Check if all values are now false
      if (Object.values(updatedCapsules).every((checked) => !checked)) {
        updatedCapsules[name] = true; // this ensures at least one capsule is checked
      }

      return updatedCapsules;
    });
  };

  return (
    <Box display="flex" flexWrap="wrap" marginLeft={2} gap={1}>
      {data.map((name) => {
        const isChecked = checkedCapsules[name];
        return (
          <Chip
            key={name}
            label={name}
            onClick={() => handleCheckChange(name)}
            icon={
              isChecked ? (
                <CheckCircleRoundedIcon style={{ color: abyssColors.primary[500] }} />
              ) : (
                <PanoramaFishEyeRoundedIcon style={{ color: abyssColors.primary[500] }} />
              )
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              height: '24px',
              border: `1px solid ${abyssColors.primary[300]}`,
            }}
          />
        );
      })}
    </Box>
  );
};
